/**
 * Armchair generator — a fully upholstered armchair built from box geometry.
 *
 * Coordinate convention: Z-up, 1 unit = 1 metre.
 *   - X = width (left–right, sitter faces +Y)
 *   - Y = depth (front–back)
 *   - Z = height
 *
 * Local anchor: origin at the geometric centre of the footprint at Z = 0.
 *
 * The armchair is a compound group (like the chair generator) so that seat,
 * back, armrests, and legs can each be individually selected and assigned
 * different materials — critical for fabric-vs-wood reading in pose reference.
 *
 * Key differences from the chair generator:
 *   - Wider, deeper seat with a thicker cushion (padded upholstery look)
 *   - Thick padded back with a more reclined default angle
 *   - Armrests are wide padded panels that run the full seat depth, not just
 *     a post + pad: each arm is a solid outer wall box with a padded top cap
 *   - Shorter block legs (often hidden under the upholstery body)
 *
 * Armrest styles:
 *   panel  — a solid side wall running the full seat depth (classic club chair)
 *   rolled — a narrow outer wall with a tall round-topped cap (Chesterfield)
 *            approximated as a wide box with proportionally tall top cap
 *
 * Back styles:
 *   solid  — a single flat back panel
 *   tufted — three horizontal divisions approximating a tufted cushion look
 *            (three equal-height sub-panels with small gaps)
 */

import type { PrimitiveGeometry } from "../primitives.ts";
import {
  buildBox,
  concatBoxes,
  concatPrimitiveGeometries,
  transformGeometry,
} from "./generatorGeom.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorChild,
  GeneratorLocalTransform,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import {
  defineGenerator,
  enumParam,
  numParam,
  paramsFromSpecs,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

interface ArmchairParams extends GeneratorParams {
  seatWidth: number;
  seatDepth: number;
  seatHeight: number;
  seatThickness: number;
  backHeight: number;
  backAngle: number;
  backStyle: string;
  backThickness: number;
  armStyle: string;
  armWidth: number;
  armHeight: number;
  legHeight: number;
  legSize: number;
}

const ARMCHAIR_SPECS: readonly GeneratorParamSpec[] = [
  {
    type: "section",
    id: "armchair-seat",
    label: "Seat",
    params: [
      {
        id: "seatWidth",
        label: "Width",
        type: "number",
        unit: "meters",
        default: 0.78,
        min: 0.5,
        max: 1.2,
        step: 0.01,
      },
      {
        id: "seatDepth",
        label: "Depth",
        type: "number",
        unit: "meters",
        default: 0.82,
        min: 0.5,
        max: 1.2,
        step: 0.01,
      },
      {
        id: "seatHeight",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.44,
        min: 0.3,
        max: 0.7,
        step: 0.01,
      },
      {
        id: "seatThickness",
        label: "Thickness",
        type: "number",
        unit: "meters",
        default: 0.14,
        min: 0.06,
        max: 0.25,
        step: 0.01,
      },
    ],
  },
  {
    type: "section",
    id: "armchair-back",
    label: "Back",
    params: [
      {
        id: "backHeight",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.55,
        min: 0.2,
        max: 1.0,
        step: 0.025,
      },
      {
        id: "backAngle",
        label: "Angle",
        type: "number",
        unit: "degrees",
        default: 15,
        min: 0,
        max: 35,
        step: 1,
      },
      {
        id: "backStyle",
        label: "Style",
        type: "enum",
        default: "solid",
        options: [
          { value: "solid", label: "Solid Panel" },
          { value: "tufted", label: "Tufted" },
        ],
      },
      {
        id: "backThickness",
        label: "Thickness",
        type: "number",
        unit: "meters",
        default: 0.14,
        min: 0.06,
        max: 0.25,
        step: 0.01,
      },
    ],
  },
  {
    type: "section",
    id: "armchair-arms",
    label: "Armrests",
    params: [
      {
        id: "armStyle",
        label: "Style",
        type: "enum",
        default: "panel",
        options: [
          { value: "panel", label: "Flat Panel" },
          { value: "rolled", label: "Rolled Arm" },
        ],
      },
      {
        id: "armWidth",
        label: "Width",
        type: "number",
        unit: "meters",
        default: 0.18,
        min: 0.08,
        max: 0.35,
        step: 0.01,
      },
      {
        id: "armHeight",
        label: "Arm Height",
        type: "number",
        unit: "meters",
        default: 0.2,
        min: 0.05,
        max: 0.4,
        step: 0.01,
        description: "Height of the arm surface above the seat.",
      },
    ],
  },
  {
    type: "section",
    id: "armchair-legs",
    label: "Legs",
    defaultCollapsed: true,
    params: [
      {
        id: "legHeight",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.12,
        min: 0.04,
        max: 0.3,
        step: 0.01,
      },
      {
        id: "legSize",
        label: "Size",
        type: "number",
        unit: "meters",
        default: 0.07,
        min: 0.03,
        max: 0.14,
        step: 0.005,
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function lt(pos: [number, number, number]): GeneratorLocalTransform {
  return { position: pos, rotation: [0, 0, 0], scale: [1, 1, 1] };
}

/**
 * Build the back panel geometry in child-local space.
 * Origin = hinge at Z = 0. Geometry extends upward to Z = backHeight.
 * For "tufted": three equal horizontal sub-panels with 0.01 m gaps.
 *
 * `seatWidth` is the inner clear width between the armrests (= the cushion
 * width), so the panel spans the full seatWidth with no subtraction.
 */
function buildBackPanel(
  seatWidth: number,
  backHeight: number,
  backThickness: number,
  backStyle: string,
): PrimitiveGeometry {
  const panelW = seatWidth;

  if (backStyle === "tufted") {
    const gap = 0.012;
    const n = 3;
    const panelH = (backHeight - gap * (n - 1)) / n;
    const parts: PrimitiveGeometry[] = [];
    for (let i = 0; i < n; i++) {
      const cz = panelH * 0.5 + i * (panelH + gap);
      parts.push(
        transformGeometry(
          buildBox(panelW, backThickness, Math.max(0.01, panelH)),
          [0, 0, cz],
        ),
      );
    }
    return concatPrimitiveGeometries(parts);
  }

  // Solid panel
  return transformGeometry(buildBox(panelW, backThickness, backHeight), [
    0,
    0,
    backHeight * 0.5,
  ]);
}

/**
 * Build one armrest assembly in child-local space.
 * Origin at the outer-bottom corner of the arm (outer X face, Z = 0 at floor).
 *
 * panel style: a solid side-wall box running seat-depth with a flat cap.
 * rolled style: the outer wall is narrower, topped with a tall rounded-top cap
 *               approximated by two stacked boxes (trapezoid silhouette).
 *
 * The armrest body runs from Z = legHeight to Z = seatHeight + armHeight.
 * The top cap is a thin pad on top.
 */
function buildArmrest(
  seatDepth: number,
  seatHeight: number,
  legHeight: number,
  armWidth: number,
  armHeight: number,
  armStyle: string,
): PrimitiveGeometry {
  const totalH = seatHeight + armHeight;
  const bodyH = Math.max(0.02, totalH - legHeight);
  const bodyCZ = legHeight + bodyH * 0.5;
  const capT = 0.04; // flat top cap thickness
  const capCZ = totalH + capT * 0.5;

  if (armStyle === "rolled") {
    // Main body (slightly narrower than the arm width)
    const bodyW = armWidth * 0.7;
    // Rolled top cap (wider than body, slightly taller, to suggest a rounded arm)
    const rollCapH = armHeight * 0.4;
    const rollCapCZ = seatHeight + rollCapH * 0.5;
    return concatBoxes([
      { cx: 0, cy: 0, cz: bodyCZ, sx: bodyW, sy: seatDepth, sz: bodyH },
      {
        cx: 0,
        cy: 0,
        cz: rollCapCZ,
        sx: armWidth,
        sy: seatDepth,
        sz: rollCapH,
      },
    ]);
  }

  // panel style — full-width body + thin cap
  return concatBoxes([
    { cx: 0, cy: 0, cz: bodyCZ, sx: armWidth, sy: seatDepth, sz: bodyH },
    { cx: 0, cy: 0, cz: capCZ, sx: armWidth, sy: seatDepth, sz: capT },
  ]);
}

// ---------------------------------------------------------------------------
// Build
// ---------------------------------------------------------------------------

function buildArmchair(
  p: ArmchairParams,
  _ctx: GeneratorBuildContext,
): GeneratorOutput {
  const seatW = numParam(p, "seatWidth", 0.78);
  const seatD = numParam(p, "seatDepth", 0.82);
  const seatH = numParam(p, "seatHeight", 0.44);
  const seatT = numParam(p, "seatThickness", 0.14);
  const backH = numParam(p, "backHeight", 0.55);
  const backAng = numParam(p, "backAngle", 15) * (Math.PI / 180);
  const backSty = enumParam(p, "backStyle", "solid");
  const backT = numParam(p, "backThickness", 0.14);
  const armSty = enumParam(p, "armStyle", "panel");
  const armW = numParam(p, "armWidth", 0.18);
  const armH = numParam(p, "armHeight", 0.2);
  const legH = numParam(p, "legHeight", 0.12);
  const legSz = numParam(p, "legSize", 0.07);

  const children: GeneratorChild[] = [];

  // ── Legs ──────────────────────────────────────────────────────────────────
  // Four corner legs, mostly hidden under the arm/seat body.
  const legXOff = seatW * 0.5 + armW - legSz * 0.5;
  const legYOff = seatD * 0.5 - legSz * 0.5;
  const legCZ = legH * 0.5;
  const legGeo = buildBox(legSz, legSz, legH);
  for (const [rid, name, sx, sy] of [
    ["leg_fl", "Leg FL", -legXOff, legYOff],
    ["leg_fr", "Leg FR", legXOff, legYOff],
    ["leg_bl", "Leg BL", -legXOff, -legYOff],
    ["leg_br", "Leg BR", legXOff, -legYOff],
  ] as [string, string, number, number][]) {
    children.push({
      roleId: rid,
      name,
      objectKind: "cube",
      geometry: legGeo,
      localTransform: lt([sx, sy, legCZ]),
    });
  }

  // ── Seat cushion ──────────────────────────────────────────────────────────
  // `seatW` is the inner clear width between the armrests, so the cushion
  // fills it exactly. Arm inner face = seatW/2; arm outer face = seatW/2 + armW.
  const cushionW = seatW;
  children.push({
    roleId: "seat",
    name: "Seat",
    objectKind: "cube",
    geometry: buildBox(cushionW, seatD, seatT),
    localTransform: lt([0, 0, seatH - seatT * 0.5]),
  });

  // ── Armrests ──────────────────────────────────────────────────────────────
  // Each arm is a solid panel assembly; its local origin is at the arm's
  // centreline (cx = 0 in child space), positioned at ±(seatW/2 + armW/2).
  const armGeo = buildArmrest(seatD, seatH, legH, armW, armH, armSty);
  const armXOff = seatW * 0.5 + armW * 0.5;

  children.push({
    roleId: "arm_l",
    name: "Armrest L",
    objectKind: "cube",
    geometry: armGeo,
    localTransform: lt([-armXOff, 0, 0]),
  });
  children.push({
    roleId: "arm_r",
    name: "Armrest R",
    objectKind: "cube",
    geometry: armGeo,
    localTransform: lt([armXOff, 0, 0]),
  });

  // ── Back ──────────────────────────────────────────────────────────────────
  // Child origin = hinge at the rear of the seat top, matching chair generator.
  // Y placement: +Y face of back at Y = -(seatD/2), so child Y = -(seatD/2 + backT/2).
  const backGeo = buildBackPanel(seatW, backH, backT, backSty);

  children.push({
    roleId: "back",
    name: "Back",
    objectKind: "cube",
    geometry: backGeo,
    localTransform: {
      position: [0, -(seatD * 0.5 + backT * 0.5), seatH],
      rotation: [backAng, 0, 0],
      scale: [1, 1, 1],
    },
  });

  return {
    kind: "group",
    parent: { name: "Armchair" },
    children,
  };
}

// ---------------------------------------------------------------------------
// Generator definition
// ---------------------------------------------------------------------------

export const ARMCHAIR_GENERATOR: Generator = defineGenerator<ArmchairParams>({
  id: "armchair",
  label: "Armchair",
  category: "prop",
  paramSpecs: ARMCHAIR_SPECS,
  defaultParams: paramsFromSpecs(ARMCHAIR_SPECS) as ArmchairParams,
  build: buildArmchair,
});
